import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { ArrowLeft, Loader2, Users, Wand2 } from "lucide-react";
import {
  AuthModal,
  GenerateButton,
  MobileBottomBar,
  PromptInput,
  UpgradePlansModal,
} from "@/components/ui";
import { CharacterGrid } from "@/components/features/characters";
import { PiecanNotice } from "@/components/features/piecan-notice";
import {
  POSITION_PRESETS,
  type PositionPreset,
} from "@/components/features/images/position-presets";
import { useActiveGenerations, useAssets } from "@/hooks";
import {
  getDisplayUrl,
  hasAccessToPlanLevel,
  useUserStore,
  type PlanType,
} from "@/stores/user-store";
import { AnalyticsService, trackPageView } from "@/services/analytics";
import { cn } from "@/lib/utils";
import type { Asset } from "@/types";

const IMAGE_EDIT_CREDIT_COST = 5;

/**
 * Dedicated edit flow for an existing character. Lives under
 * `/characters/edit` so the experience stays self-contained — no
 * detour into the generic `/images/image-edit` page, no unrelated
 * upload/asset-picker chrome. Two states:
 *
 *   1. No `?name=` query param  → character picker (grid).
 *   2. With `?name=` query param → edit view: canonical image at
 *      top, click-to-branch strip of previous versions, prompt
 *      textarea, Edit button. The result is bound to the same
 *      character folder so videos and future tweaks stay grouped.
 */
export function EditCharacterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSignedIn, user } = useUser();
  const { credits, plan } = useUserStore();
  const { hasActiveGeneration } = useActiveGenerations();
  const hasUnlimitedConcurrent = hasAccessToPlanLevel(plan, "pro");

  const characterName = searchParams.get("name") ?? null;
  const fromIdParam = searchParams.get("fromId") ?? null;

  const [prompt, setPrompt] = useState("");
  // Locally-selected source: defaults to canonical (first image),
  // but clicking a history thumbnail branches the next edit off that
  // older version instead. Reset whenever the character changes.
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(
    fromIdParam,
  );
  // Optional position preset for Qwen4Play. When set we send the
  // leetspeak `triggerWord` to the backend (which prepends it at
  // position 0 of the final prompt) AND append the natural-language
  // `readableName` to the user's prompt so the edit instruction
  // reads cleanly.
  const [selectedPosition, setSelectedPosition] =
    useState<PositionPreset | null>(null);
  // Locks the Edit button on press so a double-click can't fire two
  // generations. Reset on character/source change so re-selecting
  // the picker after a submission doesn't keep the button disabled.
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    setSelectedSourceId(fromIdParam);
    setPrompt("");
    setSelectedPosition(null);
    setIsSubmitting(false);
  }, [characterName, fromIdParam]);

  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    preset: "no-credits" | "concurrent-limit";
    creditsNeeded?: number;
  }>({ open: false, preset: "no-credits" });
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    trackPageView("Edit Character", "Edit Character", user?.id);
  }, [user?.id]);

  const { data: assetsData, isFetching } = useAssets({
    folder: characterName ?? undefined,
    bare: false,
    perPage: 200,
  });

  // The /assets API returns rows newest-first. Take only IMAGE rows
  // (videos belong to Animations, not the edit history). The first
  // is the canonical; everything after is history.
  const images = useMemo<Asset[]>(() => {
    return (assetsData?.assets ?? []).filter((a) => a.media_type === "IMAGE");
  }, [assetsData]);
  const canonical = images[0] ?? null;
  const history = images.slice(1);

  const activeSource = useMemo<Asset | null>(() => {
    if (selectedSourceId) {
      const found = images.find((a) => a.id === selectedSourceId);
      if (found) return found;
    }
    return canonical;
  }, [images, selectedSourceId, canonical]);

  // ─── Picker view ─────────────────────────────────────────────────
  if (!characterName) {
    return (
      <div className="p-6 md:p-8 max-w-4xl mx-auto">
        <h2 className="text-base font-medium text-white mb-4">
          Choose a character to edit
        </h2>
        <CharacterGrid
          onCreateClick={() => navigate("/characters")}
          // Keep the user inside the edit flow when they pick a card,
          // rather than bouncing them out to the detail page.
          onSelect={(name) =>
            navigate(`/characters/edit?name=${encodeURIComponent(name)}`)
          }
        />
      </div>
    );
  }

  // ─── Edit view ───────────────────────────────────────────────────
  const isValid =
    prompt.trim().length > 0 && !!activeSource && !isSubmitting;

  const handleEdit = () => {
    if (!isValid || !activeSource) return;

    if (!isSignedIn) {
      setShowAuthModal(true);
      return;
    }

    if (credits < IMAGE_EDIT_CREDIT_COST) {
      AnalyticsService.trackOutOfCredits({
        credits_remaining: credits,
        credits_required: IMAGE_EDIT_CREDIT_COST,
        model_attempted: "qwen_image_edit",
        generation_type: "image",
        is_paid_user: plan !== "free",
        user_plan: plan,
      });
      setUpgradeModal({
        open: true,
        preset: "no-credits",
        creditsNeeded: IMAGE_EDIT_CREDIT_COST,
      });
      return;
    }

    if (hasActiveGeneration && !hasUnlimitedConcurrent) {
      setUpgradeModal({ open: true, preset: "concurrent-limit" });
      return;
    }

    // Append the readable position name to the user's edit
    // instruction so qwen-image-edit understands the act in plain
    // English. The leetspeak `triggerWord` is sent separately as
    // `position_trigger_word` — the backend prepends it at position
    // 0 of the final prompt to activate Qwen4Play's per-act
    // checkpoints.
    let finalPrompt = prompt.trim();
    if (selectedPosition?.readableName) {
      finalPrompt = `${finalPrompt}, ${selectedPosition.readableName}`;
    }
    setIsSubmitting(true);
    navigate("/generation/new", {
      state: {
        modelId: "qwen_image_edit",
        prompt: finalPrompt,
        type: "image",
        generateParams: {
          model_id: "qwen_image_edit",
          prompt: finalPrompt,
          input_generation_id: activeSource.id,
          // Bind the resulting edit to the same character so it lands
          // in the canonical/history list automatically.
          folder: characterName,
          position_trigger_word: selectedPosition?.triggerWord || undefined,
        },
      },
    });
  };

  return (
    <>
      <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-5 pb-24 md:pb-0">
        {/* Partner upsell — points upload-capable users to PieCan for the
            image-upload flow. Compact so the editor stays primary. */}
        <PiecanNotice variant="compact" source="character_page" />

        {/* Selection header — name + a link back to the picker. */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/characters/edit")}
            title="Pick a different character"
            className="flex items-center justify-center size-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-[#aaa] hover:text-white transition-colors"
          >
            <ArrowLeft className="size-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#666] uppercase tracking-wide">
              Editing
            </p>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white line-clamp-2">
              {characterName}
            </h1>
          </div>
        </div>

        {/* Source preview — what the next edit will branch off of.
            Sized as a compact thumbnail (not a hero) so the prompt
            input and history strip stay above the fold. */}
        <div className="mx-auto w-full max-w-[16rem] rounded-2xl overflow-hidden bg-black border border-white/[0.08]">
          {isFetching && !activeSource ? (
            <div className="aspect-[2/3] animate-pulse bg-white/[0.03]" />
          ) : activeSource ? (
            <img
              src={getDisplayUrl(activeSource, plan)}
              alt={characterName}
              className="w-full max-h-[32vh] object-contain"
            />
          ) : (
            <div className="aspect-[2/3] flex flex-col items-center justify-center text-[#555] gap-2 p-4 text-center">
              <Users className="size-8" />
              <p className="text-xs">No image found for this character.</p>
            </div>
          )}
        </div>

        {/* Edit-history strip — click to branch a fresh edit off an
            older version instead of the canonical. */}
        {history.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-[#888]">
              Branch from a previous version
            </p>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {/* Canonical chip first so users can re-select it after
                  picking a history entry. */}
              {canonical && (
                <HistoryChip
                  asset={canonical}
                  label="Latest"
                  isSelected={(activeSource?.id ?? null) === canonical.id}
                  onSelect={() => setSelectedSourceId(null)}
                  plan={plan}
                />
              )}
              {history.map((h) => (
                <HistoryChip
                  key={h.id}
                  asset={h}
                  isSelected={(activeSource?.id ?? null) === h.id}
                  onSelect={() => setSelectedSourceId(h.id)}
                  plan={plan}
                />
              ))}
            </div>
          </div>
        )}

        <PromptInput
          value={prompt}
          onChange={setPrompt}
          placeholder="Describe the change. e.g., change to red lingerie, swap to a beach background, add a leather jacket..."
        />

        {/* Position presets — optional. Picking one sends the
            Qwen4Play leetspeak trigger to the backend (which
            prepends it at position 0 of the prompt) AND appends the
            natural-language phrase to the user's instruction so
            qwen-image-edit understands the act. Click again to
            deselect. */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-[#888]">Position</label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {POSITION_PRESETS.map((position) => {
              const isSelected = selectedPosition?.id === position.id;
              return (
                <button
                  key={position.id}
                  type="button"
                  onClick={() =>
                    setSelectedPosition(isSelected ? null : position)
                  }
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-xl text-xs font-medium transition-all border",
                    isSelected
                      ? "bg-white/[0.1] border-white/[0.2] text-white"
                      : "bg-white/[0.03] border-white/[0.06] text-[#888] hover:bg-white/[0.06] hover:text-white",
                  )}
                >
                  <div className="w-full aspect-square rounded-lg overflow-hidden flex items-center justify-center bg-white/[0.05]">
                    {position.thumbnail && (
                      <img
                        src={position.thumbnail}
                        alt={position.name}
                        className={cn(
                          "w-3/4 h-3/4 object-contain transition-opacity",
                          isSelected ? "opacity-100" : "opacity-40",
                        )}
                        loading="lazy"
                      />
                    )}
                  </div>
                  {position.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="hidden md:block">
          <GenerateButton
            disabled={!isValid}
            className="w-full"
            onClick={handleEdit}
          >
            <Wand2 className="size-5" />
            Edit Character ({IMAGE_EDIT_CREDIT_COST} credits)
          </GenerateButton>
        </div>
      </div>

      <MobileBottomBar>
        <GenerateButton
          disabled={!isValid}
          className="w-full"
          onClick={handleEdit}
        >
          <Wand2 className="size-5" />
          Edit Character ({IMAGE_EDIT_CREDIT_COST} credits)
        </GenerateButton>
      </MobileBottomBar>

      <UpgradePlansModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, preset: "no-credits" })}
        preset={upgradeModal.preset}
        presetContext={{
          creditsNeeded: upgradeModal.creditsNeeded,
          currentCredits: credits,
        }}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}

interface HistoryChipProps {
  asset: Asset;
  label?: string;
  isSelected: boolean;
  onSelect: () => void;
  plan: PlanType;
}

function HistoryChip({
  asset,
  label,
  isSelected,
  onSelect,
  plan,
}: HistoryChipProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      title={label ?? "Branch from this version"}
      className={cn(
        "group relative aspect-[3/4] rounded-lg overflow-hidden bg-black border transition-all",
        isSelected
          ? "border-violet-400/80 ring-2 ring-violet-400/40"
          : "border-white/[0.06] hover:border-white/[0.2]",
      )}
    >
      <img
        src={getDisplayUrl(asset, plan)}
        alt={label ?? "Previous version"}
        loading="lazy"
        className="absolute inset-0 size-full object-cover group-hover:scale-[1.03] transition-transform"
      />
      {asset.status !== "completed" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <Loader2 className="size-4 text-white animate-spin" />
        </div>
      )}
      {label && (
        <div className="absolute inset-x-0 bottom-0 px-2 py-1 bg-gradient-to-t from-black/80 to-transparent text-[10px] text-white">
          {label}
        </div>
      )}
    </button>
  );
}
